import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/bg.mp4";

function Toast({ show, message, type, onClose }) {
  if (!show) return null;

  return (
    <div
      className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
        type === "success" ? "bg-teal-600" : "bg-red-600"
      }`}
    >
      <div className="flex items-center gap-4">
        <span>{message}</span>
        <button onClick={onClose} className="text-white/80 hover:text-white">
          X
        </button>
      </div>
    </div>
  );
}

export default function TenantPaymentForm({ onPaymentSuccess }) {
  const navigate = useNavigate();
  const [balances, setBalances] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [paymentMode, setPaymentMode] = useState("full");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [proof, setProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const selectedBalance = useMemo(
    () =>
      balances.find(
        (item) => String(item.property_id) === String(selectedPropertyId)
      ),
    [balances, selectedPropertyId]
  );

  const remainingBalance = Number(selectedBalance?.remaining_balance || 0);

  useEffect(() => {
    const fetchBalances = async () => {
      setLoadingBalance(true);
      try {
        const res = await api.get("/payments/summary");
        const data = Array.isArray(res.data) ? res.data : [];
        setBalances(data);
        if (data.length > 0) {
          setSelectedPropertyId(String(data[0].property_id));
        }
      } catch (err) {
        console.error(err);
        showToast("Unable to load your rental balance", "error");
      } finally {
        setLoadingBalance(false);
      }
    };

    fetchBalances();
  }, []);

  useEffect(() => {
    if (paymentMode === "full") {
      setAmount(remainingBalance > 0 ? remainingBalance.toFixed(2) : "");
    } else {
      setAmount((Number(selectedBalance?.expected_amount || 0) / 2).toFixed(2));
    }
  }, [paymentMode, remainingBalance, selectedPropertyId, selectedBalance]);

  useEffect(() => {
    if (!proof || !proof.type.startsWith("image/")) {
      setProofPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(proof);
    setProofPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [proof]);

  const handleFileChange = (e) => setProof(e.target.files[0] || null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numericAmount = Number(amount);
    if (!selectedBalance) {
      showToast("No linked rental property found", "error");
      return;
    }

    if (!numericAmount || numericAmount <= 0) {
      showToast("Enter a valid payment amount", "error");
      return;
    }

    if (numericAmount > remainingBalance) {
      showToast("Amount cannot exceed your remaining balance", "error");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("property_id", selectedBalance.property_id);
      formData.append("amount", numericAmount);
      formData.append("payment_mode", paymentMode);
      formData.append("method", method);
      formData.append("reference_number", referenceNumber);
      formData.append("notes", notes);
      if (proof) formData.append("proof", proof);

      await api.post("/payments", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setPaymentMode("full");
      setReferenceNumber("");
      setNotes("");
      setProof(null);

      const res = await api.get("/payments/summary");
      const data = Array.isArray(res.data) ? res.data : [];
      setBalances(data);

      if (onPaymentSuccess) onPaymentSuccess();
      showToast("Payment submitted for admin review", "success");
    } catch (err) {
      console.error(err);
      showToast(
        err.response?.data?.message || "Failed to submit payment",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30";

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <video data-workspace-background="true" aria-hidden="true"
        src={bgVideo}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/86" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80" />

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: toast.type })}
      />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 text-white sm:px-6 lg:px-8">
        <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(420px,1fr)] lg:items-center">
          <div className="hidden lg:block">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Tenant Payment
            </p>
            <h1 className="mt-3 text-4xl font-bold tracking-wide">
              Submit Payment
            </h1>
            <p className="mt-3 max-w-xl text-slate-300">
              Payments are linked to your assigned rental. Partial payments stay
              pending until the admin verifies the proof.
            </p>

          </div>

          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="w-full rounded-lg border border-white/10 bg-slate-950/92 p-5 shadow-2xl sm:p-6 md:p-8"
          >
            <div className="mb-6 flex items-start justify-between gap-4 border-b border-white/10 pb-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-teal-300 lg:hidden">
                  Tenant Payment
                </p>
                <h2 className="mt-1 text-2xl font-bold text-white">
                  Submit Payment
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Full or partial payment for your linked rental.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
              >
                Back
              </button>
            </div>

            {loadingBalance ? (
              <div className="rounded-lg border border-white/10 bg-white/5 py-8 text-center text-slate-300">
                Loading rental balance...
              </div>
            ) : balances.length === 0 ? (
              <div className="rounded-lg border border-amber-300/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                No active rental property is linked to this tenant account yet.
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="tenantpaymentform-field-1" className="mb-2 block text-sm font-semibold text-slate-200">
                    Linked Rental Property
                  </label>
                  <select id="tenantpaymentform-field-1"
                    value={selectedPropertyId}
                    onChange={(e) => setSelectedPropertyId(e.target.value)}
                    className={inputClass}
                  >
                    {balances.map((item) => (
                      <option key={item.property_id} value={item.property_id}>
                        {item.property_name} - {item.location}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-white/10 bg-white/5 p-1">
                  {["full", "partial"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setPaymentMode(mode)}
                      className={`rounded-md px-4 py-2 text-sm font-semibold capitalize transition ${
                        paymentMode === mode
                          ? "bg-teal-600 text-white"
                          : "text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="tenantpaymentform-field-2" className="mb-2 block text-sm font-semibold text-slate-200">
                      Amount
                    </label>
                    <input id="tenantpaymentform-field-2"
                      type="number"
                      min="1"
                      max={remainingBalance || undefined}
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      readOnly
                      required
                      className={`${inputClass} ${
                        paymentMode === "full" ? "bg-slate-200" : ""
                      }`}
                      aria-describedby="payment-amount-help"
                      placeholder="Enter amount"
                    />
                    <p id="payment-amount-help" className="mt-2 text-sm text-slate-400">Partial payment is fixed at 50% of monthly rent. If it exceeds your balance, choose Full.</p>
                  </div>

                  <div>
                    <label htmlFor="tenantpaymentform-field-3" className="mb-2 block text-sm font-semibold text-slate-200">
                      Payment Method
                    </label>
                    <select id="tenantpaymentform-field-3"
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                      className={inputClass}
                    >
                      <option value="Cash">Cash</option>
                      <option value="GCash">GCash</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="tenantpaymentform-field-4" className="mb-2 block text-sm font-semibold text-slate-200">
                    Reference Number
                  </label>
                  <input id="tenantpaymentform-field-4"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className={inputClass}
                    placeholder="Receipt or transaction reference"
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-semibold text-slate-200">
                    Proof of Payment
                  </label>
                  <div className="rounded-lg border border-dashed border-white/15 bg-white/5 p-4">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                      key={proof ? proof.name : "file-input"}
                      className="w-full text-sm text-slate-300 file:mr-4 file:rounded-lg file:border-0 file:bg-teal-600 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-teal-500"
                    />

                    {proof && (
                      <div className="mt-4 rounded-lg border border-white/10 bg-slate-950/80 p-3">
                        <p className="text-sm font-semibold text-white">
                          {proof.name}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {(proof.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        {proofPreview && (
                          <img
                            src={proofPreview}
                            alt="Proof preview"
                            className="mt-3 max-h-56 w-full rounded object-contain"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="tenantpaymentform-field-5" className="mb-2 block text-sm font-semibold text-slate-200">
                    Notes (optional)
                  </label>
                  <textarea id="tenantpaymentform-field-5"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                    className={`${inputClass} resize-none`}
                    placeholder="Add additional information"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || remainingBalance <= 0}
                  className="mt-6 w-full rounded-lg bg-teal-600 py-3 text-lg font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Submitting..." : "Submit Payment"}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
